/**
 * Deal API
 * Uses standardized ApiResponse<T> wrapper for all endpoints
 */

import { apiGet, apiPost, apiPut, apiDelete, apiGetWithFallback, ApiError } from './api-response';
import { API, buildPath } from './api-endpoints';
import { Deal, DealMilestone } from '../types/deal';
import { logger } from '@/services/logger.service';
import apiClient from '@/services/api-client';

// Check if server is online
export async function checkHealth(): Promise<boolean> {
  try {
    await apiClient.get('/system/health');
    return true;
  } catch (error) {
    logger.warn('Health check failed', { error });
    return false;
  }
}

// Get all deals with graceful fallback
export async function getDeals(): Promise<Deal[]> {
  const data = await apiGetWithFallback<{ deals: Deal[] }>(API.deals.list.path, { deals: [] });
  return data.deals ?? [];
}

// Get a single deal
export async function getDeal(id: string): Promise<Deal> {
  try {
    const data = await apiGet<{ deal: Deal }>(buildPath(API.deals.getById.path, { id }));
    return data.deal;
  } catch (error: any) {
    logger.error('Error fetching deal', { id, error: error.message });
    throw error;
  }
}

// Create a new deal
export async function createDeal(dealData: Partial<Deal>): Promise<Deal> {
  try {
    const data = await apiPost<{ deal: Deal }>(API.deals.create.path, dealData);
    return data.deal;
  } catch (error: any) {
    logger.error('Error creating deal', { error: error.message });
    throw error;
  }
}

// Update an existing deal
export async function updateDeal(id: string, dealData: Partial<Deal>): Promise<Deal> {
  try {
    const data = await apiPut<{ deal: Deal }>(buildPath(API.deals.update.path, { id }), dealData);
    return data.deal;
  } catch (error: any) {
    logger.error('Error updating deal', { id, error: error.message });
    throw error;
  }
}

// Delete a deal
export async function deleteDeal(id: string): Promise<void> {
  try {
    await apiDelete<void>(buildPath(API.deals.delete.path, { id }));
  } catch (error: any) {
    logger.error('Error deleting deal', { id, error: error.message });
    throw error;
  }
}

// Get milestones for a deal
export async function getDealMilestones(dealId: string): Promise<DealMilestone[]> {
  try {
    const data = await apiGet<{ milestones: DealMilestone[] }>(buildPath(API.deals.getMilestones.path, { id: dealId }));
    return data.milestones ?? [];
  } catch (error: any) {
    logger.error('Error fetching milestones', { dealId, error: error.message });
    throw error;
  }
}

// Create a milestone for a deal
export async function createMilestone(dealId: string, milestoneData: Partial<DealMilestone>): Promise<DealMilestone> {
  try {
    const data = await apiPost<{ milestone: DealMilestone }>(buildPath(API.deals.getMilestones.path, { id: dealId }), milestoneData);
    return data.milestone;
  } catch (error: any) {
    logger.error('Error creating milestone', { dealId, error: error.message });
    throw error;
  }
}

// Update a milestone
export async function updateMilestone(id: string, milestoneData: Partial<DealMilestone>): Promise<DealMilestone> {
  try {
    const data = await apiPut<{ milestone: DealMilestone }>(buildPath(API.milestones.update.path, { id }), milestoneData);
    return data.milestone;
  } catch (error: any) {
    logger.error('Error updating milestone', { id, error: error.message });
    throw error;
  }
}

// Seed deals with sample data (for initial setup)
export async function seedDeals(deals: Deal[], milestones: DealMilestone[]): Promise<void> {
  try {
    const result = await apiPost<{ dealsCount: number; milestonesCount: number }>('/seed-deals', {
      deals,
      milestones,
    });
    logger.info(`Seeded ${result.dealsCount} deals and ${result.milestonesCount} milestones`);
  } catch (error: any) {
    logger.error('Error seeding deals', { error: error.message });
    throw error;
  }
}

export const dealAPI = {
  checkHealth,
  getDeals,
  getDeal,
  createDeal,
  updateDeal,
  deleteDeal,
  getDealMilestones,
  createMilestone,
  updateMilestone,
  seedDeals,
};
